import { useEffect, useMemo, useState } from 'react';
import { Ruler, Download } from '@/components/icons';
import JsBarcode from 'jsbarcode';
import QRCode from 'qrcode';
import { Button } from '@/components/ui/Button';
import {
  buildCalibrationHtml,
  buildLabelPrintHtml,
  escapeHtml,
  openPrintWindow,
  THERMAL_LABEL_PRESETS,
} from '@/lib/labelPrint';
import { LabelsPerRowToggle } from '@/components/ui/LabelsPerRowToggle';

export interface AdjustLabelItem {
  name: string;
  code: string;
  category?: string | null;
  unit?: string | null;
}

interface Props {
  open: boolean;
  serials: string[];
  item: AdjustLabelItem | null;
  onClose: () => void;
}

type LabelSize = typeof THERMAL_LABEL_PRESETS[number]['key'] | 'custom';
const SIZES = THERMAL_LABEL_PRESETS;

function genBarcodeSvgUrl(code: string): string {
  try {
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    JsBarcode(svg, code, {
      format: 'CODE128',
      width: 1.8,
      height: 40,
      displayValue: false,
      margin: 4,
      background: '#ffffff',
      lineColor: '#000000',
    });
    return 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(new XMLSerializer().serializeToString(svg));
  } catch {
    return '';
  }
}

/**
 * Barcode / QR label sheet for stock added via a manual Adjustment. Mirrors the
 * GRN label modal (same thermal-label engine and sizes) but takes a flat list
 * of already-minted serials for a single item — the ones returned by the adjust
 * call — so there's no fetch.
 */
export function AdjustLabelsModal({ open, serials, item, onClose }: Props) {
  const [labelSize, setLabelSize] = useState<LabelSize>('60x40');
  const [perRow, setPerRow] = useState<1 | 2>(1);
  const [customW, setCustomW] = useState('60');
  const [customH, setCustomH] = useState('40');
  const [previewBarcodeUrl, setPreviewBarcodeUrl] = useState('');
  const [previewQrUrl, setPreviewQrUrl] = useState('');

  const firstSerial = serials[0] ?? '';

  useEffect(() => {
    if (!open || !firstSerial) {
      setPreviewBarcodeUrl('');
      setPreviewQrUrl('');
      return;
    }
    setPreviewBarcodeUrl(genBarcodeSvgUrl(firstSerial));
    (async () => {
      setPreviewQrUrl(await QRCode.toDataURL(firstSerial, { width: 200, margin: 1, color: { dark: '#000000', light: '#ffffff' } }));
    })();
  }, [open, firstSerial]);

  const esc = escapeHtml;

  function resolvedSize() {
    const w = labelSize === 'custom' ? Math.max(10, Number(customW) || 60) : SIZES.find((s) => s.key === labelSize)!.w;
    const h = labelSize === 'custom' ? Math.max(10, Number(customH) || 40) : SIZES.find((s) => s.key === labelSize)!.h;
    // "2 labels" splits the chosen size into two equal labels side by side.
    const across = perRow;
    return { w: w / across, h, across, fullW: w };
  }

  function singleLabelHtml(fw: number, fh: number, serialNo: string, barcodeUrl: string, qrUrl: string): string {
    const s = Math.min(fw / 60, fh / 40);
    const baseFs = Math.max(3.5, 6 * s);
    const name = item?.name ?? '';
    const nameLen = name.length;
    const nameFs =
      nameLen > 40 ? Math.max(3.5, (baseFs - 1) * (40 / nameLen)) :
      nameLen > 24 ? Math.max(3.5, baseFs) :
                     baseFs + 1;
    const codeLen = serialNo.length;
    const codeFs = codeLen > 22 ? baseFs + 1 : codeLen > 14 ? baseFs + 2 : baseFs + 4;
    const shortSide = Math.min(fw, fh);
    const qrSide = shortSide * 0.40;
    const bcHeight = shortSide * 0.28;
    const qrCaption = serialNo.length > 14 ? serialNo.slice(0, 13) + '…' : serialNo;
    return `
<div class="cell">
  <div class="top-row">
    <div class="info">
      <div class="item-name" style="font-size:${nameFs.toFixed(2)}pt">${esc(name)}</div>
      ${item?.category ? `<div class="item-cat" style="font-size:${baseFs.toFixed(2)}pt">${esc(item.category)}</div>` : ''}
      <div class="serial-no" style="font-size:${codeFs.toFixed(2)}pt">${esc(serialNo)}</div>
    </div>
    <div class="qr-block">
      <img src="${qrUrl}" style="width:${qrSide.toFixed(2)}mm;height:${qrSide.toFixed(2)}mm" />
      <div class="qr-text" style="font-size:${Math.max(2.5, baseFs - 2).toFixed(2)}pt;max-width:${qrSide.toFixed(2)}mm">${esc(qrCaption)}</div>
    </div>
  </div>
  <div class="barcode-row">
    <img src="${barcodeUrl}" style="max-height:${bcHeight.toFixed(2)}mm" />
    <div class="barcode-caption" style="font-size:${Math.max(3, baseFs - 1).toFixed(2)}pt">${esc(serialNo)}</div>
  </div>
</div>`;
  }

  function printCalibration() {
    const { w, h, across } = resolvedSize();
    openPrintWindow(buildCalibrationHtml({ w, h, across }), w, h);
  }

  // Build the full, self-contained label sheet (every barcode + QR inlined as a
  // data URL) — shared by Print and Download so both produce an identical sheet.
  async function buildSheetHtml(): Promise<{ html: string; w: number; h: number } | null> {
    if (!serials.length) return null;
    const { w, h, across } = resolvedSize();
    const padding = { top: 2, right: 3, bottom: 2, left: 3 };
    const cells: string[] = [];
    for (const serialNo of serials) {
      const barcodeUrl = genBarcodeSvgUrl(serialNo);
      const qrUrl = await QRCode.toDataURL(serialNo, { width: 200, margin: 1, color: { dark: '#000000', light: '#ffffff' } });
      cells.push(singleLabelHtml(w, h, serialNo, barcodeUrl, qrUrl));
    }
    const extraCss = `
      .item-name { font-weight: normal; line-height: 1.25; overflow-wrap: break-word; word-break: break-word; }
      .item-cat  { color: #333; }
      .serial-no { font-weight: 900; margin-top: 1mm; overflow-wrap: break-word; word-break: break-all; line-height: 1.15; }
      .qr-text   { color: #555; text-align: center; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
      .barcode-caption { text-align: center; font-weight: 600; letter-spacing: 0.4px; margin-top: 0.4mm; overflow-wrap: break-word; word-break: break-all; line-height: 1.2; }
    `;
    const html = buildLabelPrintHtml({
      w, h, across, padding, extraCss,
      title: `Adjustment Labels — ${item?.code ?? ''}`,
      cellsHtml: cells.join(''),
    });
    return { html, w, h };
  }

  async function printLabels() {
    const sheet = await buildSheetHtml();
    if (!sheet) return;
    openPrintWindow(sheet.html, sheet.w, sheet.h);
  }

  async function downloadLabels() {
    const sheet = await buildSheetHtml();
    if (!sheet) return;
    // Self-contained HTML sheet — opens print-exact in any browser, from where
    // it can be printed or saved as PDF. Zero external dependencies.
    const blob = new Blob([sheet.html], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Adjustment-Labels-${item?.code ?? 'stock'}-${serials.length}.html`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }

  const { w, h, across } = resolvedSize();
  const isPortrait = h > w;
  const PX = 4;
  const s = Math.min(w / 60, h / 40);
  const baseFs = Math.max(3.5, 6 * s);
  const shortSide = Math.min(w, h);
  const qrSide = shortSide * 0.40;
  const bcHeight = shortSide * 0.28;

  const previewName = useMemo(() => item?.name ?? '', [item]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[600] flex items-center justify-center bg-black/75 p-5 backdrop-blur-sm">
      <div className="w-full max-w-3xl overflow-hidden rounded-2xl border border-[var(--border-color)] bg-[var(--modal-bg)]">
        <div className="flex items-center justify-between border-b border-[var(--border-color)] px-5 py-3 bg-[var(--bg-card)]">
          <div>
            <div className="text-sm font-extrabold text-[var(--text-primary)]">
              Adjustment Labels {item ? `— ${item.code}` : ''}
            </div>
            <div className="text-[11px] text-[var(--text-muted)]">
              {serials.length} barcode label{serials.length === 1 ? '' : 's'} — print, paste on the units, then scan them in. Same series as GRN, no overlap.
            </div>
          </div>
          <button onClick={onClose} className="flex h-7 w-7 items-center justify-center rounded-lg opacity-50 hover:opacity-100 bg-[var(--bg-soft)]">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {serials.length === 0 ? (
          <div className="p-8 text-center text-sm text-[var(--text-muted)]">No labels to print.</div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_1fr]">
            {/* Serial list */}
            <div className="max-h-[60vh] overflow-y-auto border-b lg:border-b-0 lg:border-r border-[var(--border-color)] p-4">
              <div className="mb-2 text-[11px] font-extrabold uppercase tracking-widest text-[var(--text-muted)]">
                {item?.name} — {serials.length} serial{serials.length === 1 ? '' : 's'}
              </div>
              <div className="flex flex-wrap gap-1">
                {serials.map((sn) => (
                  <span key={sn} className="rounded bg-white/[0.06] px-1.5 py-0.5 font-mono text-[10px] text-[var(--text-secondary)]">{sn}</span>
                ))}
              </div>
            </div>

            {/* Preview + size */}
            <div className="p-4">
              <div className="mb-2 text-[11px] font-extrabold uppercase tracking-widest text-[var(--text-muted)]">Label Preview</div>
              <div
                className="bg-white text-black font-sans shadow-lg mx-auto relative"
                style={{ width: w * PX, height: h * PX, border: '1.5px solid #555', boxSizing: 'border-box', padding: `${2 * PX}px ${3 * PX}px ${2 * PX}px ${3 * PX}px`, display: 'flex', flexDirection: 'column', gap: 1 * PX, overflow: 'hidden' }}
              >
                <div style={{ display: 'flex', flexDirection: isPortrait ? 'column' : 'row', gap: 1.5 * PX, alignItems: isPortrait ? 'stretch' : 'flex-start', flex: 1, minHeight: 0 }}>
                  {isPortrait && previewQrUrl && <img src={previewQrUrl} alt="QR" style={{ width: qrSide * PX, height: qrSide * PX, alignSelf: 'center' }} />}
                  <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.4 * PX, overflow: 'hidden', textAlign: isPortrait ? 'center' : 'left' }}>
                    <div style={{ fontSize: (baseFs + 1) * 1.33, lineHeight: 1.25, overflowWrap: 'break-word', wordBreak: 'break-word' }}>{previewName}</div>
                    {item?.category && <div style={{ fontSize: baseFs * 1.33, color: '#333' }}>{item.category}</div>}
                    <div style={{ fontSize: (baseFs + 4) * 1.33, fontWeight: 900, marginTop: 1 * PX, overflowWrap: 'break-word', wordBreak: 'break-all', lineHeight: 1.15 }}>{firstSerial}</div>
                  </div>
                  {!isPortrait && previewQrUrl && <img src={previewQrUrl} alt="QR" style={{ width: qrSide * PX, height: qrSide * PX }} />}
                </div>
                {previewBarcodeUrl && (
                  <div style={{ flexShrink: 0, width: '100%' }}>
                    <img src={previewBarcodeUrl} alt="barcode" style={{ width: '100%', display: 'block', maxHeight: bcHeight * PX }} />
                    <div style={{ fontSize: Math.max(3, baseFs - 1) * 1.33, textAlign: 'center', fontWeight: 600, letterSpacing: 0.4, marginTop: 0.4 * PX, overflowWrap: 'break-word', wordBreak: 'break-all', lineHeight: 1.2 }}>{firstSerial}</div>
                  </div>
                )}
              </div>

              <div className="mt-4">
                <div className="mb-1.5 text-[11px] font-bold text-[var(--text-secondary)]">Print Size</div>
                <div className="grid grid-cols-2 gap-2">
                  {SIZES.map((sz) => (
                    <button
                      key={sz.key}
                      onClick={() => setLabelSize(sz.key)}
                      className="rounded-lg px-3 py-2 text-[11px] font-bold border transition"
                      style={{ background: labelSize === sz.key ? 'var(--accent)' : 'var(--bg-card)', color: labelSize === sz.key ? '#000' : 'var(--text-secondary)', borderColor: labelSize === sz.key ? 'var(--accent)' : 'var(--border-color)' }}
                    >
                      {sz.label}
                    </button>
                  ))}
                  <button
                    onClick={() => setLabelSize('custom')}
                    className="col-span-2 rounded-lg px-3 py-2 text-[11px] font-bold border transition"
                    style={{ background: labelSize === 'custom' ? 'var(--accent)' : 'var(--bg-card)', color: labelSize === 'custom' ? '#000' : 'var(--text-secondary)', borderColor: labelSize === 'custom' ? 'var(--accent)' : 'var(--border-color)' }}
                  >
                    Custom size
                  </button>
                </div>
                {labelSize === 'custom' && (
                  <div className="mt-2 flex items-center gap-2">
                    <input type="number" min="10" max="300" value={customW} onChange={(e) => setCustomW(e.target.value)} className="w-20 rounded-md border border-[var(--border-color)] bg-[var(--bg-card)] px-2 py-1 text-xs text-center text-[var(--text-primary)]" placeholder="W" />
                    <span className="text-xs text-[var(--text-muted)]">mm ×</span>
                    <input type="number" min="10" max="300" value={customH} onChange={(e) => setCustomH(e.target.value)} className="w-20 rounded-md border border-[var(--border-color)] bg-[var(--bg-card)] px-2 py-1 text-xs text-center text-[var(--text-primary)]" placeholder="H" />
                    <span className="text-xs text-[var(--text-muted)]">mm</span>
                  </div>
                )}
                <LabelsPerRowToggle value={perRow} onChange={setPerRow} w={resolvedSize().fullW} h={resolvedSize().h} />
              </div>
            </div>
          </div>
        )}

        <div className="flex items-center justify-between gap-2 border-t border-[var(--border-color)] px-5 py-3 bg-[var(--bg-card)]">
          <div className="text-[11px] text-[var(--text-muted)]">
            {serials.length > 0 && <>{serials.length} label{serials.length === 1 ? '' : 's'} @ <span className="font-extrabold text-[var(--text-primary)]">{w} × {h} mm</span> · {across > 1 ? `same label on all ${across} stickers of a row` : '1 label per sticker'}</>}
          </div>
          <div className="flex gap-2">
            <Button variant="glass" size="sm" onClick={onClose}>Close</Button>
            <Button variant="warning" size="sm" onClick={printCalibration} title="Prints one sized rectangle with corner marks so you can verify the sticker size"><Ruler size={13} className="mr-1 inline-block align-[-2px]" /> Calibration</Button>
            <Button variant="glass" size="sm" onClick={downloadLabels} disabled={serials.length === 0} title="Save the label sheet as a self-contained HTML file (open it to print or Save as PDF)"><Download size={13} className="mr-1 inline-block align-[-2px]" /> Download</Button>
            <Button size="sm" onClick={printLabels} disabled={serials.length === 0}>Print {serials.length > 0 ? `(${serials.length})` : ''}</Button>
          </div>
        </div>
      </div>
    </div>
  );
}
